import React, { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { useBookLoader, TrameTemplateInfosByStyle, TrameBackgroundInfosByStyle, StickerInfosByStyle, CoverInfosByStyle } from '@/hooks/useBookLoader';
import { useYearbook } from '@/hooks/useYearbook';
import { parseTemplateObjects, ApiTemplateObject } from '@/lib/parseTemplateObjects';
import Sidebar from './Sidebar';
import FloatingToolbar from './FloatingToolbar';
import Canvas from './Canvas';
import OverviewView from './OverviewView';
import PreviewView from './PreviewView';
import { BookOpen, Undo2, Redo2, Loader2, FileText, Grid3x3, Eye, ChevronDown, Send, ZoomIn, ZoomOut, PanelLeftClose, PanelLeftOpen, Save } from 'lucide-react';

import SubmitDialog from './SubmitDialog';
import EditFormatDialog from './EditFormatDialog';
import { Pencil } from 'lucide-react';
import AdminSettingsMenu from './AdminSettingsMenu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ViewMode } from '@/types/yearbook';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { editorUrl } from '@/lib/editorBasePath';

const EditorLayout: React.FC = () => {
  const { bookPages, initialLoadedIndices, trameTemplateInfos, trameBackgroundInfos, stickerInfos, coverInfos, coverName: initialCoverName, favoritesColors, socialNetworkName, pictureToUse, allFolders, yearbookQuantity, withTrombi, plateforme, yearbookId, defaultFont, lockedPageNumbers, onlyPageIds, role, formatDisplay, priceWithPromoDisplay, priceWithoutPromoDisplay, formatOfYearbook, currentFormat, socialNetworkId, picturesCount, isLoading } = useBookLoader();
  const isWememberz = plateforme === 'wememberz';

  if (isLoading || !bookPages) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-editor-canvas gap-4">
        <Loader2 className="animate-spin text-editor-sidebar-active" size={40} />
        <p className="text-sm text-muted-foreground font-medium">Chargement de l'album…</p>
      </div>
    );
  }

  return <EditorContent initialPages={bookPages} preloadedIndices={initialLoadedIndices} trameTemplateInfos={trameTemplateInfos} trameBackgroundInfos={trameBackgroundInfos} stickerInfos={stickerInfos} coverInfos={coverInfos} initialCoverName={initialCoverName} initialFavoritesColors={favoritesColors} socialNetworkName={socialNetworkName} initialPictureToUse={pictureToUse} allFolders={allFolders} yearbookQuantity={yearbookQuantity} withTrombi={withTrombi} isWememberz={isWememberz} yearbookId={yearbookId} defaultFont={defaultFont} lockedPageNumbers={lockedPageNumbers} onlyPageIds={onlyPageIds} role={role} formatDisplay={formatDisplay} priceWithPromoDisplay={priceWithPromoDisplay} priceWithoutPromoDisplay={priceWithoutPromoDisplay} formatOfYearbook={formatOfYearbook} currentFormat={currentFormat} socialNetworkId={socialNetworkId} picturesCount={picturesCount} />;
};

const viewTabs: { id: ViewMode; icon: React.ReactNode; label: string }[] = [
  { id: 'page', icon: <FileText size={14} />, label: 'Éditer' },
  { id: 'overview', icon: <Grid3x3 size={14} />, label: 'Vue d\'ensemble' },
  { id: 'preview', icon: <Eye size={14} />, label: 'Aperçu' },
];
const PageNavigator: React.FC<{
  pages: import('@/types/yearbook').YearbookPage[];
  currentPageIndex: number;
  onSelectPage: (index: number) => void;
  compact?: boolean;
  coverName?: string;
}> = ({ pages, currentPageIndex, onSelectPage, compact, coverName }) => {
  const interiorPages = useMemo(
    () => pages.map((p, i) => ({ page: p, index: i })).filter((item) => !item.page.coverType),
    [pages]
  );
  const currentPage = pages[currentPageIndex];
  const isCover = !!currentPage?.coverType;
  const coverSuffix = coverName ? ` (${coverName})` : '';
  const currentLabel = isCover
    ? (currentPage.coverType === 'front'
        ? '1ère de couv.' + coverSuffix
        : currentPage.coverType === 'back'
        ? '4ème de couv.'
        : 'Tranche')
    : `Page ${currentPage?.pageNumber}`;
  const totalInterior = interiorPages.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex items-center gap-1.5 text-xs font-medium text-editor-sidebar-foreground hover:text-editor-sidebar-active transition-colors ${compact ? '' : 'ml-2 border-l border-editor-sidebar-border pl-3'}`}>
          {currentLabel} / {totalInterior}
          <ChevronDown size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={compact ? 'center' : 'start'} className="max-h-64 overflow-y-auto">
        {pages.map((page, index) => {
          const label = page.coverType === 'front'
            ? '1ère de couverture' + (coverName ? ` (${coverName})` : '')
            : page.coverType === 'back'
            ? '4ème de couverture'
            : page.coverType === 'tranche'
            ? 'Tranche'
            : `Page ${page.pageNumber}`;
          return (
            <DropdownMenuItem
              key={page.id}
              onClick={() => onSelectPage(index)}
              className={index === currentPageIndex ? 'bg-accent font-semibold' : ''}
            >
              {label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ViewSwitcher: React.FC<{
  viewMode: ViewMode;
  onViewChange: (v: ViewMode) => void;
}> = ({ viewMode, onViewChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const idx = viewTabs.findIndex((t) => t.id === viewMode);
    const btn = btnRefs.current[idx];
    const container = containerRef.current;
    if (btn && container) {
      const cRect = container.getBoundingClientRect();
      const bRect = btn.getBoundingClientRect();
      setIndicator({ left: bRect.left - cRect.left, width: bRect.width });
    }
  }, [viewMode]);

  return (
    <div
      ref={containerRef}
      className="relative flex items-center bg-black/30 rounded-full p-1 border border-white/10"
    >
      <div
        className="absolute top-1 bottom-1 rounded-full bg-primary-gradient shadow-lg transition-all duration-300 ease-out pointer-events-none"
        style={{ left: indicator.left, width: indicator.width }}
      />
      {viewTabs.map((tab, i) => (
        <button
          key={tab.id}
          ref={(el) => { btnRefs.current[i] = el; }}
          onClick={() => onViewChange(tab.id)}
          className={`relative z-10 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors duration-300 whitespace-nowrap ${
            viewMode === tab.id
              ? 'text-primary-foreground'
              : 'text-editor-sidebar-foreground/70 hover:text-editor-sidebar-foreground'
          }`}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
};

const EditorContent: React.FC<{
  initialPages: import('@/types/yearbook').YearbookPage[];
  preloadedIndices: Set<number>;
  trameTemplateInfos: TrameTemplateInfosByStyle;
  trameBackgroundInfos: TrameBackgroundInfosByStyle;
  stickerInfos: StickerInfosByStyle;
  coverInfos: CoverInfosByStyle;
  initialCoverName: string;
  initialFavoritesColors: string[];
  socialNetworkName: string | null;
  initialPictureToUse: Set<number>;
  allFolders: import('@/hooks/useBookLoader').AllFolder[];
  yearbookQuantity: number;
  withTrombi: boolean;
  isWememberz: boolean;
  yearbookId: number | null;
  defaultFont: string;
  lockedPageNumbers: Set<number>;
  onlyPageIds: number[];
  role: string;
  formatDisplay: string;
  priceWithPromoDisplay: string;
  priceWithoutPromoDisplay: string;
  formatOfYearbook: string;
  currentFormat: string;
  socialNetworkId: number | null;
  picturesCount: number | null;
}> = ({ initialPages, preloadedIndices, trameTemplateInfos, trameBackgroundInfos, stickerInfos, coverInfos, initialCoverName, initialFavoritesColors, socialNetworkName, initialPictureToUse, allFolders, yearbookQuantity, withTrombi, isWememberz, yearbookId, defaultFont, lockedPageNumbers, onlyPageIds, role, formatDisplay, priceWithPromoDisplay, priceWithoutPromoDisplay, formatOfYearbook, currentFormat, socialNetworkId, picturesCount }) => {
  const {
    pages,
    currentPageIndex,
    setCurrentPageIndex,
    currentPage,
    selectedElementId,
    setSelectedElementId,
    selectedElementIds,
    setSelectedElementIds,
    selectedElement,
    selectedElements,
    activeTool,
    setActiveTool,
    addTextElement,
    addImageElement,
    addStickerElement,
    addQrCodeElement,
    addElements,
    addShapeElement,
    updateElement,
    updateElementDrag,
    updateMultipleElementsDrag,
    deleteElement,
    deleteMultipleElements,
    duplicateElement,
    duplicateMultipleElements,
    moveElementLayer,
    setPageBackground,
    applyLayout,
    applyCover,
    dropImage,
    undo,
    redo,
    canUndo,
    canRedo,
    beginDrag,
    endDrag,
    loadedIndices,
    reorderPage,
    swapPages,
    renamePage,
    resetPage,
    duplicatePageContent,
    setPageContent,
    resetMultiplePages,
    addPageGuide,
    deletePageGuide,
    removeGuideEverywhere,
    loadAllPages,
    ensureIndicesLoaded,
    isSaving,
  } = useYearbook(initialPages, preloadedIndices, defaultFont, lockedPageNumbers);

  // Add a guide: on a single page, or on every free page of the book. In the
  // "all pages" case the persistence is done server-side in one request; the
  // in-memory pages only show the guide (no save_page POST is triggered).
  const handleAddPageGuide = useCallback(
    (pageIndex: number, guide: { orientation: 'horizontal' | 'vertical'; position: number; applyToAll?: boolean }) => {
      const id = addPageGuide(pageIndex, guide);
      if (!guide.applyToAll) return;
      fetch(editorUrl('/add_guide_all_pages'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guide: { id, orientation: guide.orientation, position: guide.position },
        }),
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          toast.success('Repère ajouté à toutes les pages');
        })
        .catch(() => {
          // Roll back the local display: the guide was not persisted.
          removeGuideEverywhere(id);
          toast.error("Impossible d'appliquer le repère à toutes les pages");
        });
    },
    [addPageGuide, removeGuideEverywhere]
  );



  // Compute pictureToUse dynamically from all pages so it stays in sync
  // after slot clear, replace, reset page, layout change, etc.
  const pictureToUse = React.useMemo(() => {
    const ids = new Set<number>();
    for (const page of pages) {
      for (const el of page.elements) {
        if (el.pictureId) ids.add(el.pictureId);
      }
    }
    return ids;
  }, [pages]);
  const [coverName, setCoverName] = useState<string>(initialCoverName);
  const [favColors, setFavColors] = useState<string[]>(initialFavoritesColors);
  const [viewMode, setViewMode] = useState<ViewMode>('page');
  const [viewTransitioning, setViewTransitioning] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [submitValidation, setSubmitValidation] = useState<{ allowed: boolean; admin: string[] } | null>(null);
  const [zoomMultiplier, setZoomMultiplier] = useState(1);
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // Scrolling title: measure overflow so the marquee only runs when needed.
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const titleTextRef = useRef<HTMLHeadingElement>(null);
  const [titleOverflow, setTitleOverflow] = useState(0);
  // Wememberz: editable album name (pencil next to the title).
  const [networkName, setNetworkName] = useState<string | null>(socialNetworkName);
  useLayoutEffect(() => {
    const measure = () => {
      const container = titleContainerRef.current;
      const text = titleTextRef.current;
      if (!container || !text) return;
      const diff = text.scrollWidth - container.clientWidth;
      setTitleOverflow(diff > 2 ? diff : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [networkName]);

  const ZOOM_STEP = 0.15;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2.5;

  const handleZoomIn = React.useCallback(() => {
    setZoomMultiplier((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100));
  }, []);
  const handleZoomOut = React.useCallback(() => {
    setZoomMultiplier((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100));
  }, []);
  const handleZoomReset = React.useCallback(() => setZoomMultiplier(1), []);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const openRename = useCallback(() => {
    setRenameValue(networkName ?? '');
    setRenameOpen(true);
  }, [networkName]);

  const handleRename = useCallback(async () => {
    const name = renameValue.trim();
    if (!name || !socialNetworkId) return;
    setRenaming(true);
    try {
      const res = await fetch(`/social_networks/${socialNetworkId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ social_network: { name } }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNetworkName(name);
      setRenameOpen(false);
      toast.success('Nom mis à jour');
    } catch {
      toast.error('Erreur lors du renommage');
    } finally {
      setRenaming(false);
    }
  }, [renameValue, socialNetworkId]);

  // Wememberz: start on the "Couverture" tab instead of "Inspiration".
  // If the book has no photo at all (pictures_count === 0), open the Photos tab
  // instead: its panel auto-opens the upload dialog when empty.
  const didInitWememberzTab = useRef(false);
  useEffect(() => {
    if (!isWememberz || didInitWememberzTab.current) return;
    didInitWememberzTab.current = true;
    setActiveTool(picturesCount === 0 ? 'photos' : 'cover');
    if (picturesCount === 0) {
      // Ouverture forcée de la popup d'import (indépendante du comptage des dossiers)
      setTimeout(() => window.dispatchEvent(new Event('yearbook:open-import')), 600);
    }
  }, [isWememberz, picturesCount, setActiveTool]);

  // Wememberz: when the book has no cover yet, the cover panel applies (and saves) the first one.
  const autoApplyDefaultCover = React.useMemo(() => {
    if (!isWememberz) return false;
    const front = initialPages.find((p) => p.coverType === 'front');
    return !!front && front.elements.length === 0;
    // Based on the initially loaded pages only: must not re-trigger after applying.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWememberz]);

  // Auto-switch cover <-> layout when navigating between cover and interior pages.
  // Only reacts to actual page changes (not to element clicks that switch the tab themselves).
  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;
  const selectedElementIdsRef = useRef(selectedElementIds);
  selectedElementIdsRef.current = selectedElementIds;
  const prevPageIndexRef = useRef(-1);
  useEffect(() => {
    if (prevPageIndexRef.current === currentPageIndex) return;
    prevPageIndexRef.current = currentPageIndex;
    // On first load, an empty Wememberz album must stay on Photos so the
    // upload dialog can open; the cover auto-switch used to override it.
    if (isWememberz && picturesCount === 0) return;
    // An element click already switched the tab — don't override it
    if (selectedElementIdsRef.current.length > 0) return;
    const page = pages[currentPageIndex];
    const isCover = !!page?.coverType;
    const isTranche = page?.coverType === 'tranche';
    const tool = activeToolRef.current;
    if (isTranche && tool === 'cover') {
      setActiveTool('text');
    } else if (isCover && !isTranche && tool !== 'cover') {
      setActiveTool('cover');
    } else if (!isCover && tool === 'cover') {
      setActiveTool('layout');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageIndex, pages, setActiveTool]);

  const handleAddImage = addImageElement;
  const handleDeleteElement = deleteElement;
  const handleDropImage = dropImage;

  const handleViewChange = React.useCallback(
    (newView: ViewMode) => {
      if (newView === viewMode || viewTransitioning) return;
      // Pre-load all pages when entering preview or overview
      if (newView === 'preview' || newView === 'overview') {
        loadAllPages();
      }
      setViewTransitioning(true);
      setTimeout(() => {
        setViewMode(newView);
        setViewTransitioning(false);
      }, 200);
    },
    [viewMode, viewTransitioning, loadAllPages]
  );

  const handleOverviewSwitchToPage = React.useCallback(
    async (index: number) => {
      if (viewTransitioning) return;
      setViewTransitioning(true);
      await ensureIndicesLoaded([index]);
      setTimeout(() => {
        setCurrentPageIndex(index);
        setViewMode('page');
        setViewTransitioning(false);
      }, 200);
    },
    [viewTransitioning, setCurrentPageIndex, ensureIndicesLoaded]
  );

  // Auto-switch sidebar tab based on selected element(s)
  const handleSelectElement = React.useCallback(
    (id: string | null, metaKey?: boolean, pageIndex?: number) => {
      if (id === null) {
        setSelectedElementIds([]);
        return;
      }

      const page = pages[pageIndex ?? currentPageIndex];
      const el = page?.elements.find((e) => e.id === id);
      // Expand selection to whole group when the clicked element belongs to one.
      const groupId = el?.groupId;
      const groupIds = groupId
        ? (page?.elements.filter((e) => e.groupId === groupId).map((e) => e.id) ?? [id])
        : [id];

      if (metaKey) {
        // Toggle group in/out of multi-selection
        setSelectedElementIds((prev) => {
          const allIn = groupIds.every((gid) => prev.includes(gid));
          if (allIn) {
            return prev.filter((x) => !groupIds.includes(x));
          }
          const merged = [...prev];
          for (const gid of groupIds) if (!merged.includes(gid)) merged.push(gid);
          return merged;
        });
      } else {
        setSelectedElementIds(groupIds);
      }

      // Auto-switch sidebar tool based on the element type (only for single select or the clicked element)
      if (!metaKey) {
        const elType = el?.type as string | undefined;
        if (elType === 'slot' || elType === 'free_slot' || elType === 'image') {
          setActiveTool('photos');
        } else if (elType === 'text') {
          setActiveTool('text');
        } else if (elType === 'shape' || elType === 'shape_border') {
          setActiveTool('stickers');
        }
        // Other element types on a cover keep the current tab (no forced 'cover' switch)
      }
    },
    [setSelectedElementIds, pages, currentPageIndex, setActiveTool]
  );

  // Batch update: when updating style on one element, apply same style updates to all selected elements of the same type
  const handleUpdateElementBatch = React.useCallback(
    (id: string, updates: Partial<import('@/types/yearbook').YearbookElement>) => {
      updateElement(id, updates);

      // If multi-selection, apply style-related updates to siblings of same type
      if (selectedElements.length > 1) {
        const targetEl = selectedElements.find((e) => e.id === id);
        if (!targetEl) return;
        const siblings = selectedElements.filter((e) => e.id !== id && e.type === targetEl.type);
        // Only propagate style-related keys
        const styleKeys: (keyof import('@/types/yearbook').YearbookElement)[] = ['textStyle', 'content'];
        const shapeStyleKeys: (keyof import('@/types/yearbook').YearbookElement)[] = ['textStyle', 'strokeWidth'];
        const relevantKeys = targetEl.type === 'text' ? styleKeys : shapeStyleKeys;
        const styleUpdates: Partial<import('@/types/yearbook').YearbookElement> = {};
        for (const key of relevantKeys) {
          if (key in updates) {
            (styleUpdates as any)[key] = (updates as any)[key];
          }
        }
        if (Object.keys(styleUpdates).length > 0) {
          for (const sib of siblings) {
            updateElement(sib.id, styleUpdates);
          }
        }
      }
    },
    [updateElement, selectedElements]
  );

  const handleTrombiCreated = useCallback(
    async (data: Record<string, { objects_positions: any[] }>) => {
      const genId = () => Math.random().toString(36).substring(2, 11);
      const entries = Object.entries(data);
      const pageIndices: number[] = [];
      for (const [pageNumStr] of entries) {
        const pageIdx = pages.findIndex((p) => p.pageNumber === Number(pageNumStr));
        if (pageIdx !== -1) pageIndices.push(pageIdx);
      }

      // Ensure all target pages are loaded before placing content
      if (pageIndices.length > 0) {
        await ensureIndicesLoaded(pageIndices);
      }

      for (const [pageNumStr, pageData] of entries) {
        const pageNum = Number(pageNumStr);
        const pageIdx = pages.findIndex((p) => p.pageNumber === pageNum);
        if (pageIdx === -1) continue;

        const rawPositions = pageData.objects_positions as ApiTemplateObject[];
        const { elements, background } = parseTemplateObjects(rawPositions, genId);
        setPageContent(pageIdx, elements, background, 'Trombi');
      }

      // Navigate to the first affected page
      if (pageIndices.length > 0) {
        setCurrentPageIndex(pageIndices[0]);
      }
    },
    [pages, setPageContent, ensureIndicesLoaded, setCurrentPageIndex]
  );

  /**
   * Met à jour un trombi existant : on fait matcher les objets renvoyés par l'API
   * avec les éléments déjà présents sur la page via le user_id, et on ne remplace
   * que les textes (text_template) et les photos (picture / picture_template).
   */
  const handleTrombiUpdated = useCallback(
    async (data: Record<string, { objects_positions: any[] }>) => {
      const entries = Object.entries(data ?? {});
      const pageIndices: number[] = [];
      for (const [pageNumStr] of entries) {
        const idx = pages.findIndex((p) => p.pageNumber === Number(pageNumStr));
        if (idx !== -1) pageIndices.push(idx);
      }
      if (pageIndices.length > 0) await ensureIndicesLoaded(pageIndices);

      for (const [pageNumStr, pageData] of entries) {
        const pageIdx = pages.findIndex((p) => p.pageNumber === Number(pageNumStr));
        if (pageIdx === -1) continue;
        const page = pages[pageIdx];
        if (!page) continue;

        const incoming = (pageData?.objects_positions ?? []) as ApiTemplateObject[];

        // Group incoming objects by user_id, split by kind (text vs picture)
        const texts = new Map<number, ApiTemplateObject[]>();
        const pictures = new Map<number, ApiTemplateObject[]>();
        for (const obj of incoming) {
          const uid = (obj as any).user_id;
          if (uid === undefined || uid === null) continue;
          const key = Number(uid);
          if (obj.object_type === 'text_template' || obj.object_type === 'text') {
            texts.set(key, [...(texts.get(key) ?? []), obj]);
          } else if (obj.object_type === 'picture' || obj.object_type === 'picture_template') {
            pictures.set(key, [...(pictures.get(key) ?? []), obj]);
          }
        }

        const textCursor = new Map<number, number>();
        const pictureCursor = new Map<number, number>();

        const nextElements = page.elements.map((el) => {
          const uid = el.userId;
          if (uid === undefined || uid === null) return el;
          const key = Number(uid);

          if (el.type === 'text') {
            const list = texts.get(key);
            if (!list?.length) return el;
            const i = textCursor.get(key) ?? 0;
            const obj = list[Math.min(i, list.length - 1)];
            textCursor.set(key, i + 1);
            return {
              ...el,
              content: obj.content ?? el.content,
              textStyle: el.textStyle
                ? {
                    ...el.textStyle,
                    ...(obj.fontSize ? { fontSize: parseInt(obj.fontSize, 10) } : {}),
                    ...(obj.fontFamily ? { fontFamily: obj.fontFamily } : {}),
                    ...(obj.color ? { color: obj.color } : {}),
                  }
                : el.textStyle,
            };
          }

          if (el.type === 'slot' || el.type === 'image') {
            const list = pictures.get(key);
            if (!list?.length) return el;
            const i = pictureCursor.get(key) ?? 0;
            const obj = list[Math.min(i, list.length - 1)];
            pictureCursor.set(key, i + 1);
            return {
              ...el,
              ...(obj.image_url ? { imageUrl: obj.image_url } : {}),
              ...(obj.thumbnail ? { thumbnail: obj.thumbnail } : {}),
              ...(obj.picture_id !== undefined ? { pictureId: obj.picture_id } : {}),
            };
          }

          return el;
        });

        setPageContent(pageIdx, nextElements, undefined, 'Trombi');
      }

      if (pageIndices.length > 0) setCurrentPageIndex(pageIndices[0]);
    },
    [pages, setPageContent, ensureIndicesLoaded, setCurrentPageIndex]
  );



  const handleTrombiRemoved = useCallback(
    async (pageIds: number[]) => {
      console.log('[TrombiRemove] API returned pageIds:', pageIds);

      // Build a Set of target ids for fast lookup (both string and number forms)
      const targetIds = new Set(pageIds.map(String));

      // Find matching page indices – search by page.id AND page.pageNumber
      // Use a function that reads the latest pages via the ref to avoid stale closure
      const findIndices = (pagesSnapshot: typeof pages) => {
        const indices: number[] = [];
        for (let i = 0; i < pagesSnapshot.length; i++) {
          const p = pagesSnapshot[i];
          if (targetIds.has(p.id) || targetIds.has(String(p.pageNumber))) {
            indices.push(i);
          }
        }
        return indices;
      };

      // First pass with current pages
      let pageIndices = findIndices(pages);
      console.log('[TrombiRemove] First pass found indices:', pageIndices, 'for ids:', pageIds);

      // Ensure all candidate pages are loaded (they might be lazy-loaded stubs)
      if (pageIndices.length > 0) {
        await ensureIndicesLoaded(pageIndices);
      }

      // If we didn't find all pages, it might be because page data wasn't loaded yet.
      // Try a broader search: load ALL pages that could match by scanning the full array.
      if (pageIndices.length < pageIds.length) {
        // Some pages weren't matched – load all pages as fallback
        const allIndices = Array.from({ length: pages.length }, (_, i) => i);
        await ensureIndicesLoaded(allIndices);
        // Re-scan after loading
        pageIndices = findIndices(pages);
        console.log('[TrombiRemove] Second pass (after full load) found indices:', pageIndices);
      }

      if (pageIndices.length === 0) {
        console.warn('[TrombiRemove] No matching pages found for ids:', pageIds);
        return;
      }

      setCurrentPageIndex(pageIndices[0]);
      resetMultiplePages(pageIndices, { type: 'color', value: '#FFFFFF' }, 'Blank');
    },
    [pages, resetMultiplePages, ensureIndicesLoaded, setCurrentPageIndex]
  );

  const handleApplyMagicPage = useCallback(
    async (pageIndex: number, elements: import('@/types/yearbook').YearbookElement[], background?: import('@/types/yearbook').PageBackground, layoutTemplateId?: number) => {
      await ensureIndicesLoaded([pageIndex]);
      setPageContent(pageIndex, elements, background);
    },
    [setPageContent, ensureIndicesLoaded]
  );

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden">
      {/* Top bar */}
      <header className="h-12 bg-editor-topbar flex items-center px-4 border-b border-editor-sidebar-border shrink-0 relative">
        <div className="flex items-center h-full">
          {/* Title zone sized to the sidebar width so undo/redo start at its edge.
              Logo is centered over the icon rail (68px + 1px border), title over the panel (240px). */}
          <div className={`flex items-center min-w-0 ${sidebarVisible ? 'w-[309px] -ml-4 shrink-0' : ''}`}>
            {sidebarVisible ? (
              <>
                <a
                  href="/"
                  className="flex items-center justify-center w-[69px] shrink-0 h-full hover:opacity-80 transition-opacity"
                >
                  <img src="/images_editor/favicon.png" alt="Editor icon" className="w-7 h-7 object-contain" />
                </a>
                <div className="flex items-center justify-center w-[240px] min-w-0 shrink-0 px-2">
                  <div ref={titleContainerRef} className="overflow-hidden min-w-0">
                    <h1
                      ref={titleTextRef}
                      className={`text-sm font-display font-semibold text-editor-sidebar-foreground tracking-wide whitespace-nowrap ${titleOverflow > 0 ? 'animate-marquee-if-needed' : ''}`}
                      style={titleOverflow > 0 ? ({ '--marquee-shift': `${-titleOverflow}px` } as React.CSSProperties) : undefined}
                    >
                      {networkName || 'Yearbook Editor'}
                    </h1>
                  </div>
                  {isWememberz && socialNetworkId && (
                    <button
                      onClick={openRename}
                      className="ml-1 p-1 rounded shrink-0 text-editor-sidebar-foreground hover:bg-editor-sidebar-hover hover:text-editor-sidebar-active transition-colors"
                      title="Renommer l'album"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 min-w-0 max-w-full">
                <a href="/" className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity">
                  <img src="/images_editor/favicon.png" alt="Editor icon" className="w-7 h-7 object-contain shrink-0" />
                  <div ref={titleContainerRef} className="overflow-hidden min-w-0">
                    <h1
                      ref={titleTextRef}
                      className={`text-sm font-display font-semibold text-editor-sidebar-foreground tracking-wide whitespace-nowrap ${titleOverflow > 0 ? 'animate-marquee-if-needed' : ''}`}
                      style={titleOverflow > 0 ? ({ '--marquee-shift': `${-titleOverflow}px` } as React.CSSProperties) : undefined}
                    >
                      {networkName || 'Yearbook Editor'}
                    </h1>
                  </div>
                </a>
                {isWememberz && socialNetworkId && (
                  <button
                    onClick={openRename}
                    className="p-1 rounded shrink-0 text-editor-sidebar-foreground hover:bg-editor-sidebar-hover hover:text-editor-sidebar-active transition-colors"
                    title="Renommer l'album"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>
            )}
          </div>
          {viewMode === 'page' && (
            <div className="flex items-center gap-0.5 border-l border-editor-sidebar-border pl-3 h-full shrink-0">
              <button
                onClick={undo}
                disabled={!canUndo}
                className="p-1.5 rounded text-editor-sidebar-foreground disabled:opacity-20 hover:bg-editor-sidebar-hover hover:text-editor-sidebar-active transition-colors"
                title="Annuler (Ctrl+Z)"
              >
                <Undo2 size={16} />
              </button>
              <button
                onClick={redo}
                disabled={!canRedo}
                className="p-1.5 rounded text-editor-sidebar-foreground disabled:opacity-20 hover:bg-editor-sidebar-hover hover:text-editor-sidebar-active transition-colors"
                title="Refaire (Ctrl+Y)"
              >
                <Redo2 size={16} />
              </button>
            </div>
          )}
        </div>

        {/* View mode switcher – absolutely centered */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <ViewSwitcher viewMode={viewMode} onViewChange={handleViewChange} />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {isWememberz && (formatDisplay || priceWithPromoDisplay || priceWithoutPromoDisplay) && (
            <div className="flex items-center gap-2 pr-2 mr-1 border-r border-editor-sidebar-border">
              <div className="text-right leading-tight">
                {formatDisplay && (
                  <div className="text-[11px] text-editor-sidebar-foreground/70">{formatDisplay}</div>
                )}
                {(priceWithPromoDisplay || priceWithoutPromoDisplay) && (
                  <div className="flex items-baseline justify-end gap-1.5">
                    {priceWithoutPromoDisplay && (
                      <span
                        className="text-[11px] text-editor-sidebar-foreground/50 line-through"
                        dangerouslySetInnerHTML={{ __html: priceWithoutPromoDisplay }}
                      />
                    )}
                    {priceWithPromoDisplay && (
                      <span
                        className="text-xs font-semibold text-editor-sidebar-foreground"
                        dangerouslySetInnerHTML={{ __html: priceWithPromoDisplay }}
                      />
                    )}
                  </div>
                )}
                <div className="text-[10px] text-editor-sidebar-foreground/50">Livraison offerte dés 50€</div>
              </div>
              <button
                onClick={() => setFormatDialogOpen(true)}
                title="Modifier le format"
                aria-label="Modifier le format"
                className="p-1.5 rounded text-editor-sidebar-foreground hover:bg-editor-sidebar-hover hover:text-editor-sidebar-active transition-colors"
              >
                <Pencil size={13} />
              </button>
            </div>
          )}
          {role === '1year1book' && (
            <AdminSettingsMenu pages={pages} initialOnlyPageIds={onlyPageIds} />
          )}
          <button
            onClick={async () => {
              setSubmitOpen(true);
              setSubmitValidation(null);
              try {
                const res = await fetch(editorUrl('/check_user_validation'));
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (data?.result === true) {
                  setSubmitValidation({ allowed: true, admin: [] });
                } else {
                  setSubmitValidation({ allowed: false, admin: Array.isArray(data?.admin) ? data.admin : [] });
                }
              } catch {
                // En cas d'erreur, on ne bloque pas l'utilisateur.
                setSubmitValidation({ allowed: true, admin: [] });
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary-gradient text-primary-foreground hover:opacity-90 transition-opacity"
          >
            <Send size={13} />
            {isWememberz ? 'Ajouter au panier' : 'Envoyer en vérification'}
          </button>
        </div>
      </header>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Transition overlay */}
        {viewTransitioning && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-editor-canvas/80 animate-fade-in">
            <Loader2 className="animate-spin text-editor-sidebar-active" size={28} />
          </div>
        )}

        {viewMode === 'page' && sidebarVisible && (
          <Sidebar
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            isCoverPage={!!currentPage?.coverType}
            isTranchePage={currentPage?.coverType === 'tranche'}
            onAddText={addTextElement}
            onAddImage={handleAddImage}
            onAddSticker={addStickerElement}
            onAddQrCode={addQrCodeElement}
            onAddElements={addElements}
            onAddShape={addShapeElement}
            onSetBackground={setPageBackground}
            onApplyLayout={applyLayout}
            onApplyCover={(data) => { applyCover(data); if (data.cover_name) setCoverName(data.cover_name); }}
            activeTemplateId={currentPage?.layoutTemplateId}
            activeBackgroundId={currentPage?.background?.trame_background_id}
            activeBackgroundCustomId={currentPage?.background?.trame_background_custom_id}
            selectedElement={selectedElement}
            selectedElements={selectedElements}
            onUpdateElement={handleUpdateElementBatch}
            trameTemplateInfos={trameTemplateInfos}
            trameBackgroundInfos={trameBackgroundInfos}
            initialFavoritesColors={favColors}
            onFavColorsChange={setFavColors}
            pages={pages}
            pictureToUse={pictureToUse}
            stickerInfos={stickerInfos}
            coverInfos={coverInfos}
            onTrombiCreated={handleTrombiCreated}
            onTrombiRemoved={handleTrombiRemoved}
            onTrombiUpdated={handleTrombiUpdated}
            onApplyMagicPage={handleApplyMagicPage}
            onNavigateToPage={setCurrentPageIndex}
            allFolders={allFolders}
            autoOpenPhotoImport={isWememberz && picturesCount === 0}
            withTrombi={withTrombi}
            isWememberz={isWememberz}
            autoApplyDefaultCover={autoApplyDefaultCover}
          />
        )}

        {/* Canvas column + bottom bar */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {viewMode === 'page' && (
            <Canvas
              pages={pages}
              currentPageIndex={currentPageIndex}
              selectedElementIds={selectedElementIds}
              loadedIndices={loadedIndices}
              onSelectPage={setCurrentPageIndex}
              onSelectElement={handleSelectElement}
              onUpdateElement={updateElement}
              onDragUpdateElement={updateElementDrag}
              onMultiDragUpdate={updateMultipleElementsDrag}
              onDragStart={beginDrag}
              onDragEnd={endDrag}
              onDropImage={handleDropImage}
              onRenamePage={renamePage}
              onResetPage={resetPage}
              onSwapPages={swapPages}
              onDuplicatePageContent={duplicatePageContent}
              onRemoveBackground={(idx: number) => setPageBackground('color', '#FFFFFF')}
              onAddPageGuide={handleAddPageGuide}
              onDeletePageGuide={deletePageGuide}
              onRestorePageVersion={(idx, elements, background) => setPageContent(idx, elements, background)}
              selectedElements={selectedElements}
              onDeleteElement={handleDeleteElement}
              onDeleteMultiple={deleteMultipleElements}
              onDuplicateElement={duplicateElement}
              onDuplicateMultiple={duplicateMultipleElements}
              onMoveLayer={moveElementLayer}
              zoomMultiplier={zoomMultiplier}
              onCoverPageClick={() => setActiveTool('cover')}
              yearbookQuantity={yearbookQuantity}
              coverName={coverName}
              defaultFont={defaultFont}
              lockedPageNumbers={lockedPageNumbers}
            />
          )}

          {viewMode === 'overview' && (
            <OverviewView
              pages={pages}
              currentPageIndex={currentPageIndex}
              onSelectPage={setCurrentPageIndex}
              onSwitchToPage={handleOverviewSwitchToPage}
              onReorderPage={reorderPage}
            />
          )}

          {viewMode === 'preview' && <PreviewView pages={pages} />}

          {/* Bottom bar */}
          {viewMode === 'page' && (
            <div className="h-9 shrink-0 flex items-center border-t border-editor-sidebar-border bg-editor-topbar px-3">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setSidebarVisible((v) => !v)}
                  className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-editor-sidebar-hover transition-colors text-editor-sidebar-foreground/60 hover:text-editor-sidebar-foreground"
                  title={sidebarVisible ? 'Masquer le panneau' : 'Afficher le panneau'}
                >
                  {sidebarVisible ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
                </button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center w-7 h-7 text-editor-sidebar-foreground/40">
                      {isSaving ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Save size={13} />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    {isSaving ? 'Sauvegarde en cours…' : 'Sauvegarde automatique'}
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className="flex-1 flex items-center justify-center">
                <PageNavigator
                  pages={pages}
                  currentPageIndex={currentPageIndex}
                  onSelectPage={setCurrentPageIndex}
                  compact
                  coverName={coverName}
                />
              </div>

              <div className="flex items-center gap-0.5">
                <button
                  onClick={handleZoomOut}
                  disabled={zoomMultiplier <= ZOOM_MIN}
                  className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-editor-sidebar-hover transition-colors text-editor-sidebar-foreground disabled:opacity-30"
                  title="Zoom arrière"
                >
                  <ZoomOut size={14} />
                </button>
                <button
                  onClick={handleZoomReset}
                  className="text-xs text-editor-sidebar-foreground/70 hover:text-editor-sidebar-foreground transition-colors min-w-[3rem] text-center font-mono"
                  title="Réinitialiser le zoom"
                >
                  {Math.round(zoomMultiplier * 100)}%
                </button>
                <button
                  onClick={handleZoomIn}
                  disabled={zoomMultiplier >= ZOOM_MAX}
                  className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-editor-sidebar-hover transition-colors text-editor-sidebar-foreground disabled:opacity-30"
                  title="Zoom avant"
                >
                  <ZoomIn size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} isWememberz={isWememberz} yearbookId={yearbookId} validation={submitValidation} />
      {isWememberz && socialNetworkId && (
        <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Renommer l'album</DialogTitle>
            </DialogHeader>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
              autoFocus
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Nom de l'album"
            />
            <DialogFooter>
              <button
                onClick={handleRename}
                disabled={renaming || !renameValue.trim()}
                className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-gradient text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {renaming && <Loader2 size={14} className="animate-spin" />}
                Enregistrer
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {isWememberz && (
        <EditFormatDialog
          open={formatDialogOpen}
          onOpenChange={setFormatDialogOpen}
          socialNetworkId={socialNetworkId}
          currentFormat={currentFormat || formatOfYearbook}
          currentPageCount={pages.filter((p) => !p.coverType).length}
        />
      )}
    </div>
  );
};

export default EditorLayout;